<template>
  <div id="logical">
    <!-- Header -->
    <b-button-toolbar class="mx-1 d-flex">
      <!-- Runtime -->
      <b-button-group v-if="type !== 'function'" class="ml-1" size="sm">
        <b-button
          @click="handleRuntime('start')"
          :disabled="!(!running && !('node' in $route.query))"
          class="btn-light"
          title="Start"
        >
          <font-awesome-icon icon="play" />
        </b-button>
        <b-button
          @click="handleRuntime('stop')"
          :disabled="!(running && !('node' in $route.query))"
          class="btn-light"
          title="Stop"
        >
          <font-awesome-icon icon="stop" />
        </b-button>
        <b-button
          @click="toglePreview(true)"
          :disabled="'node' in $route.query"
          class="btn-light"
          :class="{ active: preview }"
          title="Preview"
        >
          <font-awesome-icon icon="glasses" />
        </b-button>
      </b-button-group>

      <!-- Data -->
      <b-button-group class="ml-1" size="sm">
        <b-button
          @click.prevent="$refs.saveModal.show()"
          :disabled="
            !(
              !readonly &&
              editor &&
              'history' in editor &&
              editor.history.produced.length
            )
          "
          class="btn-light"
          title="Save logical"
        >
          <font-awesome-icon icon="save" />
        </b-button>
        <b-button
          @click="editor.trigger('undo')"
          :disabled="
            !(
              !readonly &&
              editor &&
              'history' in editor &&
              editor.history.produced.length
            )
          "
          class="btn-light"
          title="Undo"
        >
          <font-awesome-icon icon="undo-alt" />
        </b-button>
        <b-button
          @click="editor.trigger('redo')"
          :disabled="
            !(
              !readonly &&
              editor &&
              'history' in editor &&
              editor.history.reserved.length
            )
          "
          class="btn-light"
          title="Redo"
        >
          <font-awesome-icon icon="redo-alt" />
        </b-button>
        <b-button
          @click.prevent="showVersion"
          :disabled="readonly"
          class="btn-light"
          title="Change version"
        >
          <font-awesome-icon icon="exchange-alt" />
        </b-button>
      </b-button-group>

      <!-- Align -->
      <b-button-group class="ml-1" size="sm">
        <b-button
          @click="alignNode('left')"
          :disabled="readonly"
          class="btn-light"
          title="Align left"
        >
          <font-awesome-icon icon="align-left" />
        </b-button>

        <b-button
          @click="alignNode('top')"
          :disabled="readonly"
          class="btn-light"
          title="Align top"
        >
          <font-awesome-icon rotation="90" icon="align-left" />
        </b-button>

        <b-button
          @click="alignNode('bottom')"
          :disabled="readonly"
          class="btn-light"
          title="Align bottom"
        >
          <font-awesome-icon rotation="90" icon="align-right" />
        </b-button>

        <b-button
          @click="alignNode('right')"
          :disabled="readonly"
          class="btn-light"
          title="Align right"
        >
          <font-awesome-icon icon="align-right" />
        </b-button>

        <b-button
          @click="alignNode('vertical')"
          :disabled="readonly"
          class="btn-light"
          title="Space vertical"
        >
          <font-awesome-icon icon="align-justify" />
        </b-button>

        <b-button
          @click="alignNode('horizontal')"
          :disabled="readonly"
          class="btn-light"
          title="Space horizontal"
        >
          <font-awesome-icon rotation="90" icon="align-justify" />
        </b-button>
      </b-button-group>

      <!-- Tag -->
      <b-button-group class="ml-1" size="sm">
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="GET_R"
        >
          <font-awesome-icon icon="tag" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="SET_R"
        >
          <font-awesome-icon icon="paper-plane" />
        </b-button>
      </b-button-group>

      <!-- Comparison -->
      <b-button-group class="ml-1" size="sm">
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="EQ_R"
        >
          <font-awesome-icon icon="equals" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="GTE_R"
        >
          <font-awesome-icon icon="greater-than-equal" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="GT_R"
        >
          <font-awesome-icon icon="greater-than" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="LT_R"
        >
          <font-awesome-icon icon="less-than" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="LTE_R"
        >
          <font-awesome-icon icon="less-than-equal" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="NEQ_R"
        >
          <font-awesome-icon icon="not-equal" />
        </b-button>
      </b-button-group>

      <!-- Math -->
      <b-button-group class="ml-1" size="sm">
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="MULT_R"
        >
          <font-awesome-icon icon="times" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="DIV_R"
        >
          <font-awesome-icon icon="divide" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="ADD_R"
        >
          <font-awesome-icon icon="plus" />
        </b-button>
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="MIN_R"
        >
          <font-awesome-icon icon="minus" />
        </b-button>
      </b-button-group>

      <!-- New -->
      <!-- <b-button-group class="ml-1" size="sm">
        <b-button
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          title="ALARM"
        >
          <font-awesome-icon icon="exclamation-triangle" />
        </b-button>
      </b-button-group> -->

      <!-- Function -->
      <b-button-group v-if="type !== 'function'" class="ml-1" size="sm">
        <b-button
          v-for="func in functions"
          :key="func.id"
          :data-func="func.id"
          @click="selectNode"
          :disabled="readonly"
          class="btn-light"
          :class="{ 'd-none': func.deletedAt }"
          :title="func.name"
        >
          <font-awesome-icon icon="link" />
        </b-button>
      </b-button-group>

      <!-- Back -->
      <b-button-group
        v-if="type !== 'function' && 'node' in $route.query"
        class="ml-1"
        size="sm"
      >
        <b-button
          @click="$router.replace({ query: {} })"
          class="btn-light"
          title="Return"
        >
          <font-awesome-icon icon="chevron-circle-left" />
        </b-button>
      </b-button-group>

      <b-button-group class="ml-auto" size="sm">
        <b-button
          @click.prevent="$refs.restoreModal.show()"
          class="btn-light"
          title="Restore logical"
        >
          <font-awesome-icon icon="trash-restore" />
        </b-button>
      </b-button-group>
    </b-button-toolbar>

    <!-- Rete -->
    <div id="rete-editor" :class="{ 'd-none': readonly }" />
    <div id="rete-runtime" :class="{ 'd-none': !readonly }" />

    <!-- Save modal -->
    <b-modal
      ref="saveModal"
      title="Save logical"
      header-bg-variant="info"
      centered
      ok-title="Save"
      ok-variant="success"
      cancel-title="Cancel"
      @hide="message = ''"
      @ok="saveLogical"
      hide-header-close
    >
      <b-form-textarea
        placeholder="Create new version..."
        rows="3"
        v-model="message"
        max-rows="6"
      ></b-form-textarea>
    </b-modal>

    <!-- Version modal -->
    <b-modal
      ref="versionModal"
      size="xl"
      title="Logical version"
      header-bg-variant="info"
      centered
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      hide-header-close
    >
      <b-table-simple small bordered id="logical-table">
        <thead>
          <tr>
            <th><div>Created At</div></th>
            <th><div>Updated At</div></th>
            <th><div>Version</div></th>
            <th><div>Message</div></th>
            <th><div>Action</div></th>
          </tr>
        </thead>
        <tbody>
          <!-- Data rows -->
          <tr
            v-for="logical in versions"
            :key="logical.id"
            :class="{ selected: logical.version === version }"
          >
            <td>
              <span>{{ logical.createdAt }}</span>
            </td>
            <td>
              <span>{{ logical.updatedAt }}</span>
            </td>
            <td class="center">
              <span>{{ logical.version }}</span>
            </td>
            <td>
              <span>{{ logical.message }}</span>
            </td>
            <td class="center">
              <b-button
                :disabled="logical.version === version"
                @click="loadVersion(logical.id, logical.version)"
                size="sm"
                class="btn-info"
                title="Load"
              >
                <font-awesome-icon icon="reply" />
              </b-button>
              <b-button
                :disabled="logical.version === version"
                @click="deleteVersion(logical.id)"
                size="sm"
                class="btn-danger ml-1"
                title="Delete"
              >
                <font-awesome-icon icon="minus-circle" />
              </b-button>
            </td>
          </tr>
        </tbody>
      </b-table-simple>
    </b-modal>

    <!-- Restore modal -->
    <b-modal
      ref="restoreModal"
      size="xl"
      title="Restore logical"
      header-bg-variant="info"
      centered
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      hide-header-close
    >
      <b-table-simple small bordered id="logical-table">
        <thead>
          <tr>
            <th><div>Name</div></th>
            <th><div>Deleted At</div></th>
            <th><div>Version</div></th>
            <th><div>Message</div></th>
            <th><div>Action</div></th>
          </tr>
        </thead>
        <tbody>
          <!-- Data rows -->
          <tr v-for="logical in deleted" :key="logical.id">
            <td>
              <span>{{ logical.name }}</span>
            </td>
            <td>
              <span>{{ logical.deletedAt.Time }}</span>
            </td>
            <td class="center">
              <span>{{ logical.version }}</span>
            </td>
            <td>
              <span>{{ logical.message }}</span>
            </td>
            <td class="center">
              <b-button
                @click="createRestore(logical.id)"
                size="sm"
                class="btn-info"
                title="Restore"
              >
                <font-awesome-icon icon="recycle" />
              </b-button>
              <b-button
                @click="deleteRestore(logical.id)"
                size="sm"
                class="btn-danger ml-1"
                title="Delete"
              >
                <font-awesome-icon icon="minus-circle" />
              </b-button>
            </td>
          </tr>
        </tbody>
      </b-table-simple>
    </b-modal>
  </div>
</template>

<script>
function sortNode(index) {
  return function(a, b) {
    if (index !== undefined) {
      a = a.position[index]
      b = b.position[index]

      if (a > b) return 1
      if (b > a) return -1

      return 0
    }

    const ax = a.position[0]
    const bx = b.position[0]

    if (ax > bx) return 1
    if (bx > ax) return -1

    const ay = a.position[1]
    const by = b.position[1]

    if (ay > by) return 1
    if (ay > ay) return -1

    return 0
  }
}

import { mapGetters } from 'vuex'

import moment from 'moment'
const format = 'MMM DD, YYYY HH:mm:ss'

import Rete from 'rete'
import ConnectionPlugin from 'rete-connection-plugin'
import VueRenderPlugin from 'rete-vue-render-plugin'
// import HistoryPlugin from 'rete-history-plugin'
import HistoryPlugin from './plugins/history/index'
import ReadonlyPlugin from 'rete-readonly-plugin'
import AreaPlugin from 'rete-area-plugin'

import io from '@/socket.io'

import {
  GetTagComponent,
  SetTagComponent,
  MathComponent,
  CompareComponent,
  AlarmComponent,
  IoComponent,
  FunctionComponent,
} from './components'

export default {
  data() {
    return {
      editor: null,
      runtime: null,
      loading: false,
      logical: 0,
      type: '',
      preview: false,
      readonly: false,
      contextTarget: {},
      version: 0,
      message: '',
      versions: [],
      running: false,
      temp: {},
    }
  },

  computed: {
    ...mapGetters({
      // tagOptions: 'tag/itemsOptions',
      tagItems: 'tag/items',
      functions: 'logical/functions',
      deleted: 'logical/deleted',
    }),
  },

  async mounted() {
    await this.$store.dispatch('logical/subscribe')
    Rete.Input.prototype.showControl = function() {
      if (this.node.preview) {
        return this.control !== null
      }

      return !this.hasConnection() && this.control !== null
    }

    // Load logical
    await this.loadLogical()

    // Add key listener
    document.addEventListener('keyup', this.handleKeyUp)
  },

  methods: {
    async loadLogical() {
      this.loading = true
      const id = this.$route.params.id
      this.logical = 0

      if (!isNaN(id)) {
        this.logical = parseInt(id)

        // preview node runtime
        if ('node' in this.$route.query) {
          await this.handleRuntime()
          return
        }

        // load temp data
        if (Object.keys(this.temp).length) {
          // create editor
          await this.createRete(this.temp)
          // setup runtime
          await this.handleRuntime()
          return
        }

        // request data ke server
        const res = await this.$store.dispatch('logical/get', this.logical)

        if (Object.keys(res).length) {
          this.type = res.type
          this.version = res.version

          // create editor
          await this.createRete(res)
          // setup runtime
          await this.handleRuntime()
        }
      }
    },

    async createRete(res) {
      this.readonly = this.preview

      // readonly when node function
      if ('node' in this.$route.query) {
        this.readonly = true
      }

      let container = document.querySelector('#rete-editor')

      if (this.readonly) {
        container = document.querySelector('#rete-runtime')
      }

      container.innerHTML = ''

      const editor = new Rete.NodeEditor('demo@0.1.0', container)
      editor.use(ConnectionPlugin)
      editor.use(VueRenderPlugin)

      if (this.readonly) {
        editor.use(ReadonlyPlugin)
      }

      const getTags = []
      const setTags = []
      const boolOptions = []
      for (const key in this.tagItems) {
        const item = this.tagItems[key]
        const option = {
          text: item.name,
          value: item.uid,
        }

        if (item.deviceId) {
          getTags.push(option)
        } else {
          getTags.push(option)
          setTags.push(option)
        }

        if (item.type === 'bool') {
          boolOptions.push(key)
        }
      }

      // register component
      editor.register(new GetTagComponent(this.readonly, getTags, boolOptions))
      editor.register(new SetTagComponent(this.readonly, setTags, boolOptions))

      const math = ['ADD_R', 'MIN_R', 'MULT_R', 'DIV_R']
      math.map((name) =>
        editor.register(new MathComponent(name, this.readonly))
      )

      const compare = ['EQ_R', 'NEQ_R', 'GT_R', 'GTE_R', 'LT_R', 'LTE_R']
      compare.map((name) =>
        editor.register(new CompareComponent(name, this.readonly))
      )

      editor.register(new AlarmComponent())

      // set io component
      const ios = JSON.parse(res.io)
      for (const io in ios) {
        const component = new IoComponent(
          io.toUpperCase(),
          ios[io],
          this.readonly
        )

        editor.register(component)
      }

      // prevent zoom on double click
      editor.on('zoom', ({ source }) => {
        return source !== 'dblclick'
      })

      editor.on('nodecreated', (node) => {
        node.preview = this.preview

        // function node preview
        if (!isNaN(node.name)) {
          node.vueContext.$el.addEventListener('dblclick', () => {
            this.$router.replace({ query: { node: node.id } })
          })
        }
      })

      editor.on('click', async () => {
        // reset selected node & add node
        editor.selected.clear()
        editor.nodes.map((n) => n.update())
        this.addNode()
      })

      // Draw node items
      const nodes = {}
      JSON.parse(res.nodes).map((node) => {
        nodes[node.id] = node
      })

      const editorData = {
        id: 'demo@0.1.0',
        nodes,
      }

      // function component
      if (this.type !== 'function') {
        this.functions.map((func) => {
          const component = new FunctionComponent(
            String(func.id),
            func.name,
            JSON.parse(func.io),
            this.readonly
          )

          editor.register(component)
        })
      }

      await editor.fromJSON(editorData)

      AreaPlugin.zoomAt(editor)

      if (this.readonly) {
        this.runtime = editor
      } else {
        editor.use(HistoryPlugin, { keyboard: true })

        if ('history' in res) {
          editor.trigger('sethistory', res.history)
        }

        this.runtime = null
        this.editor = editor
      }

      // Change node function name
      this.renameFunction()
    },

    renameFunction() {
      let editor = this.editor

      if (this.readonly) {
        editor = this.runtime
      }

      if (editor) {
        for (const index in editor.nodes) {
          const node = editor.nodes[index]

          if ('funcId' in node) {
            const func = this.functions.find(
              (func) => func.id === parseInt(node.funcId)
            )

            node.name = func.name
            node.update()
            continue
          }

          if (!isNaN(node.name)) {
            const func = this.functions.find(
              (func) => func.id === parseInt(node.name)
            )

            node.name = func.name
            node.funcId = func.id
            node.update()
          }
        }
      }
    },

    async handleRuntime(a) {
      let key = this.$route.params.id

      const req = {
        id: key,
        action: a,
      }

      // apabila preview
      if (this.preview) {
        req.data = 'preview'
      }

      // apabila node function
      if ('node' in this.$route.query) {
        const node = this.$route.query.node
        key += `-${node}`
        req.node = node
      }

      const res = await this.$store.dispatch('logical/runtime', req)
      this.running = res.enable
      this.loading = false

      if (res.nodes.length) {
        await this.createRete(res)

        if (this.preview) {
          io.on(`logical-${key}`, async (payload) => {
            await this.updateRuntime(payload)
          })

          await this.updateRuntime(res.blocks)
        }

        return
      }

      if ('node' in this.$route.query) {
        this.$router.replace({ query: {} })
      }
    },

    updateRuntime(blocks = '{}') {
      // Reset connection
      const connections = document.getElementsByClassName('connection')
      connections.forEach((el) => {
        el.classList.remove('active')
      })

      // Reset socket
      const sockets = document.getElementsByClassName('socket')
      sockets.forEach((el) => {
        el.classList.remove('active')
      })

      // Parse blocks
      const nodes = JSON.parse(blocks)

      // Set preview
      this.runtime.nodes.forEach((node) => {
        const control = node.controls.get('preview')
        if (control) {
          let value = ''

          if (this.running && node.id in nodes) {
            node = nodes[node.id]

            value = node.outputs.out1

            if (value === undefined) {
              value = node.data.out1
              if (value === undefined) {
                value = ''
              }
            }
          }

          control.setValue(value)
        }
      })

      if (this.running) {
        this.runtime.view.connections.forEach((con) => {
          // Set connection input value
          let id = con.connection.input.node.id
          const node = this.runtime.nodes.find((node) => node.id == id)
          if (node) {
            const key = con.connection.input.key
            const input = node.inputs.get(key)
            if (input.control) {
              const value = nodes[id].inputs[key]
              input.control.setValue(value)
            }
          }

          // Set connection & socket color
          id = con.outputNode.node.id
          const outputs = nodes[id].outputs
          const key = con.connection.output.key

          if (key in outputs && outputs[key] !== false) {
            con.el.children[0].classList.add('active')

            con.outputNode.sockets.forEach((soc) => {
              if (soc.io.key == con.connection.output.key) {
                if (!soc.el.classList.contains('active')) {
                  soc.el.classList.add('active')
                }
              }
            })

            con.inputNode.sockets.forEach((soc) => {
              if (soc.io.key == con.connection.input.key) {
                if (!soc.el.classList.contains('active')) {
                  soc.el.classList.add('active')
                }
              }
            })
          }
        })
      }
    },

    async toglePreview(preview) {
      io.removeAllListeners()

      if (preview) {
        this.preview = !this.preview
      }

      if (!('node' in this.$route.query)) {
        // create temp data
        if (this.preview) {
          this.temp = this.getLogical()
        }

        // reset runtime
        if (!this.preview) {
          this.readonly = false
          this.runtime = null

          if (this.editor === null) {
            await this.loadLogical()
          }

          this.renameFunction()
          return
        }
      }

      await this.handleRuntime()
    },

    async handleKeyUp(e) {
      switch (e.code) {
        case 'Escape':
          this.deselectNode()
          break
        case 'Delete':
          this.deleteNode()
          break
        case 'KeyC':
          if (e.ctrlKey) {
            await this.cloneNode()
          }
          break
      }
    },

    selectNode(event) {
      if (this.contextTarget.classList) {
        this.contextTarget.classList.remove('active')
      }

      this.contextTarget = event.currentTarget
      this.contextTarget.classList.add('active')
    },

    deselectNode() {
      if (this.contextTarget.title) {
        this.contextTarget.classList.remove('active')
        this.contextTarget = {}
      }
    },

    async addNode() {
      if (this.contextTarget.title) {
        const key =
          'func' in this.contextTarget.dataset
            ? this.contextTarget.dataset.func
            : this.contextTarget.title

        const component = this.editor.components.get(key)

        if (component) {
          const n = await component.createNode()
          const position = this.editor.view.area.mouse
          n.position = [position.x, position.y]
          this.editor.addNode(n)
          this.renameFunction()

          // Reset context target
          this.deselectNode()
        }
      }
    },

    async cloneNode() {
      this.editor.selected.list.map(async (node) => {
        if (node.name !== 'INPUTS' && node.name !== 'OUTPUTS') {
          const {
            name,
            position: [x, y],
            data,
          } = node
          console.log(node)

          let component

          if ('funcId' in node) {
            component = this.editor.components.get(String(node.funcId))
          } else {
            component = this.editor.components.get(name)
          }

          if (component) {
            const n = await component.createNode()
            n.data = data
            n.position = [x + 10, y + 10]

            this.editor.addNode(n)
            this.renameFunction()
          }
        }
      })
    },

    deleteNode() {
      this.editor.selected.list.map((node) => {
        if (node.name !== 'INPUTS' && node.name !== 'OUTPUTS') {
          this.editor.removeNode(node)
        }
      })
    },

    alignNode(align) {
      const nodes = this.editor.view.nodes
      const list = this.editor.selected.list

      function translate(node, x, y) {
        if (node.position[0] !== x || node.position[1] !== y) {
          nodes.get(node).translate(x, y)
        }
      }

      if (list.length > 1) {
        let position

        let end
        let total = 0
        let space
        switch (align) {
          case 'left':
            position = list[0].position[0]

            list.map((node) => {
              translate(node, position, node.position[1])
            })
            break
          case 'top':
            position = list[0].position[1]

            list.map((node) => {
              translate(node, node.position[0], position)
            })
            break
          case 'right':
            position = list[0].position[0]

            list.map((node) => {
              translate(node, position, node.position[1])
            })
            break
          case 'bottom':
            position = list[0].position[1]
            position += list[0].vueContext.$el.offsetHeight

            list.map((node) => {
              const ty = position - node.vueContext.$el.offsetHeight
              translate(node, node.position[0], ty)
            })
            break
          case 'vertical':
            list.sort(sortNode(1))

            position = list[0].position[1]
            end = list[list.length - 1].position[1]
            end += list[list.length - 1].vueContext.$el.offsetHeight

            list.map((node) => {
              total += node.vueContext.$el.offsetHeight
            })

            space = (end - position - total) / (list.length - 1)

            list.map((node) => {
              translate(node, node.position[0], position)
              position += space + node.vueContext.$el.offsetHeight
            })
            break
          case 'horizontal':
            list.sort(sortNode(0))

            position = list[0].position[0]
            end = list[list.length - 1].position[0]
            end += list[list.length - 1].vueContext.$el.offsetWidth

            list.map((node) => {
              total += node.vueContext.$el.offsetWidth
            })

            space = (end - position - total) / (list.length - 1)

            list.map((node) => {
              translate(node, position, node.position[1])
              position += space + node.vueContext.$el.offsetWidth
            })
            break
        }
      }
    },

    getLogical() {
      if (this.editor) {
        this.editor.nodes.sort(sortNode)

        const io = {}
        this.editor.nodes.forEach((node) => {
          // Rename node function name to id
          if ('funcId' in node) {
            node.name = String(node.funcId)
          }

          switch (node.name) {
            case 'INPUTS':
              io.inputs = {}
              node.outputs.forEach((item) => {
                io.inputs[item.key] = {
                  name: item.name,
                  type: item.socket.name,
                }
              })
              break
            case 'OUTPUTS':
              io.outputs = {}
              node.inputs.forEach((item) => {
                io.outputs[item.key] = {
                  name: item.name,
                  type: item.socket.name,
                }
              })
              break
          }
        })

        return {
          id: this.logical,
          nodes: JSON.stringify(this.editor.nodes),
          io: JSON.stringify(io),
          msg: this.message,
          history: this.editor.history,
        }
      }

      return {}
    },

    async saveLogical() {
      const data = this.getLogical()
      this.renameFunction()

      await this.$store.dispatch('logical/patch', data)

      this.editor.trigger('sethistory', { produced: [], reserved: [] })
    },

    async showVersion() {
      this.versions = []

      const res = await this.$store.dispatch('logical/findV', {
        ver: this.logical,
      })

      if (res.length) {
        this.versions = res
        this.versions.map((version) => {
          version['createdAt'] = moment(version['createdAt']).format(format)
          version['updatedAt'] = moment(version['updatedAt']).format(format)
        })
      }

      this.$refs.versionModal.show()
    },

    async loadVersion(id, version) {
      const data = {
        id: this.logical,
        ver: id,
      }

      await this.$store.dispatch('logical/patch', data)

      this.version = version
      await this.loadLogical()
    },

    async deleteVersion(id) {
      const data = {
        id: this.logical,
        ver: id,
      }

      await this.$store.dispatch('logical/delete', data)

      const index = this.versions.findIndex((version) => version.id === id)
      if (index > -1) {
        this.versions.splice(index, 1)
      }
    },

    async createRestore(id) {
      await this.$store.dispatch('logical/create', { id })
    },

    async deleteRestore(id) {
      await this.$store.dispatch('logical/delete', { id })
    },
  },

  async destroyed() {
    document.removeEventListener('keyup', this.handleKeyUp)
    await this.$store.dispatch('logical/unsubscribe')
  },

  watch: {
    '$route.params.id': async function() {
      this.preview = false
      this.temp = {}

      await this.loadLogical()
    },

    '$route.query': async function() {
      await this.toglePreview()
    },
  },
}
</script>

<style lang="sass">
@import './assets/custom.sass'
@import './assets/boostrap-select.sass'
@import './assets/bootstrap.sass'
</style>
